import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatNavigatorItem } from './ChatNavigator.type';

// Reference near the viewport BOTTOM: a turn becomes active as soon as it
// scrolls into view from below ("the message shown on screen is the active
// one"), and hands back to the previous turn the moment it drops out of view
// when scrolling up
const FOCUS_OFFSET_PCT = 0.9;
// After a manual set (tick/row click), ignore scroll-driven recomputes briefly
// so the jump animation's intermediate positions don't flicker the highlight
const SUPPRESS_AFTER_MANUAL_MS = 600;

/**
 * Tracks which user turn is currently "active" in the chat viewport.
 *
 * Same mechanism as the chatgpt-conversation-timeline extension (which works
 * on ChatGPT/DeepSeek): on every scroll frame, RE-MEASURE each loaded turn's
 * top position and pick the LAST one whose top has passed a reading line at
 * ~30% of the viewport. Stateless position comparison per frame — unlike
 * IntersectionObserver crossings, nothing can be "missed" when short user
 * bubbles fly through a detection band during fast scrolls.
 *
 * Positions are measured in scroll-container space via rect deltas (no
 * positioned-ancestor assumptions). Bottom edge: at the scroll floor the last
 * loaded turn is active (turns near the end can never reach the line).
 */
export function useActiveNavMessage(
  containerRef: React.RefObject<HTMLDivElement>,
  items: ChatNavigatorItem[],
  getMessageElement: (id: string) => HTMLElement | null,
  loadedCount: number,
  oldestLoadedId: string | undefined,
  newestLoadedId?: string,
): [string | null, (id: string) => void] {
  const [activeId, setActiveId] = useState<string | null>(null);
  const rafRef = useRef<number | undefined>();
  const suppressUntilRef = useRef(0);

  // Deterministic override for events where the id is already known
  // (tick/row clicks, jump targets)
  const setActiveManually = useCallback((id: string) => {
    suppressUntilRef.current = performance.now() + SUPPRESS_AFTER_MANUAL_MS;
    setActiveId(id);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || items.length === 0) return;

    const compute = () => {
      if (performance.now() < suppressUntilRef.current) return;
      const containerRect = container.getBoundingClientRect();
      const { scrollTop, clientHeight, scrollHeight } = container;
      const referenceY = scrollTop + clientHeight * FOCUS_OFFSET_PCT;

      let active: string | null = null;
      let first: string | null = null;
      let last: string | null = null;
      for (const item of items) {
        const el = getMessageElement(item.id);
        if (!el) continue;
        if (first === null) first = item.id;
        last = item.id;
        const top =
          el.getBoundingClientRect().top - containerRect.top + scrollTop;
        if (top <= referenceY) active = item.id;
      }

      // Bottom rule: pinned at the scroll floor → the last loaded turn wins
      if (last && scrollTop + clientHeight >= scrollHeight - 4) active = last;
      // Top rule (mirror): pinned at the scroll ceiling → the first loaded
      // turn wins — otherwise a lower turn sharing the first screen keeps the
      // highlight and tick 1 is unreachable
      if (first && scrollTop <= 4) active = first;

      setActiveId(active ?? first);
    };

    const onScroll = () => {
      if (rafRef.current !== undefined) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = undefined;
        compute();
      });
    };

    compute();
    container.addEventListener('scroll', onScroll, { passive: true });

    // Layout changes (streaming growth, images, pagination) move positions
    const resizeObserver = new ResizeObserver(() => compute());
    resizeObserver.observe(container);
    if (container.firstElementChild) resizeObserver.observe(container.firstElementChild);

    return () => {
      container.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef, items, getMessageElement, loadedCount, oldestLoadedId, newestLoadedId]);

  return [activeId, setActiveManually];
}
